import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../../lib/site-data";

const links = [
    { href: "#services", label: "Services" },
    { href: "#gallery", label: "Gallery" },
    { href: "#about", label: "About" },
    { href: "#pricing", label: "Booking" },
    { href: "#testimonials", label: "Stories" },
    { href: "#faq", label: "FAQ" },
];

export const Nav = () => {
    const [pastHero, setPastHero] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setPastHero(window.scrollY > 600);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const dark = !pastHero; // on dark hero
    const linkColor = dark ? "text-white/70 hover:text-white" : "text-brand-inkSoft hover:text-brand-ink";
    const logoText = dark ? "text-white" : "text-brand-ink";
    const ctaCls = dark
        ? "bg-white text-brand-ink hover:bg-brand-coralLight hover:text-white"
        : "bg-brand-ink text-brand-cream hover:bg-brand-coral";
    const containerCls = pastHero
        ? "glass shadow-[0_8px_30px_rgba(224,122,95,0.10)]"
        : "bg-black/20 backdrop-blur-md border border-white/10";

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${pastHero ? "py-3" : "py-5"}`}
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
                <div className={`flex items-center justify-between rounded-full px-5 sm:px-7 py-3 transition-all duration-500 ${containerCls}`}>
                    <a href="#top" className="flex items-center gap-2.5" data-testid="nav-logo">
                        <img src={LOGO_URL} alt="Glow Momentz" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/30" />
                        <span className={`font-display text-xl tracking-tight hidden sm:inline ${logoText}`}>
                            Glow <span className="italic text-brand-coralLight">Momentz</span>
                        </span>
                    </a>

                    <nav className="hidden md:flex items-center gap-8">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                className={`text-sm transition-colors relative group ${linkColor}`}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                            >
                                {l.label}
                                <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-coral group-hover:w-full transition-all duration-300" />
                            </a>
                        ))}
                    </nav>

                    <a
                        href="#inquiry"
                        data-testid="nav-cta-book"
                        className={`hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors ${ctaCls}`}
                    >
                        Book me <span className="text-brand-gold">✦</span>
                    </a>

                    <button
                        onClick={() => setOpen(!open)}
                        className={`md:hidden p-2 -mr-2 ${dark ? "text-white" : "text-brand-ink"}`}
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>

                {open && (
                    <div className="md:hidden mt-2 glass rounded-3xl p-6 space-y-3" data-testid="nav-mobile-menu">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="block text-base text-brand-ink py-2 border-b border-brand-border/60 last:border-0"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#inquiry"
                            onClick={() => setOpen(false)}
                            className="block w-full text-center rounded-full bg-brand-ink text-brand-cream py-3 text-sm mt-2"
                        >
                            Book me ✦
                        </a>
                    </div>
                )}
            </div>
        </header>
    );
};

export default Nav;
